import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ChartOfAccountsModel } from '../../sifrarnik/services/chart-of-accounts-data.service';
import { CodebookItem } from '../../sifrarnik/services/codebook-lookup.service';
import { BankPostingRule, BankPostingRuleService } from '../../invoices/services/bank-posting-rule.service';
import {
  BankPostingRuleDialogComponent,
  BankPostingRuleDialogData,
} from '../bank-posting-rule-dialog/bank-posting-rule-dialog.component';

export interface BankPostingRulesListDialogData {
  clientID: string;
  companyAccount: string;
  chartOfAccounts: ChartOfAccountsModel[];
  codebookItems: CodebookItem[];
}

/**
 * "Pravila za stavke": every posting rule of the company bank account the
 * current statement belongs to, with create / edit / delete.
 * Closes with `true` when anything changed so the caller can re-apply rules.
 */
@Component({
  selector: 'app-bank-posting-rules-list-dialog',
  templateUrl: './bank-posting-rules-list-dialog.component.html',
  styleUrls: ['./bank-posting-rules-list-dialog.component.css'],
})
export class BankPostingRulesListDialogComponent implements OnInit {
  rules: BankPostingRule[] = [];
  loading = false;
  changed = false;
  deletingId: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<BankPostingRulesListDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BankPostingRulesListDialogData,
    private ruleService: BankPostingRuleService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  async ngOnInit(): Promise<void> {
    await this.reload();
  }

  async reload(): Promise<void> {
    this.loading = true;
    try {
      this.rules = await this.ruleService.listForAccount(this.data.clientID, this.data.companyAccount, true);
    } catch (err) {
      console.error('[BankPostingRules] Load failed:', err);
      this.snackBar.open('Loading rules failed', '', { duration: 4000, panelClass: ['white-snackbar'] });
    } finally {
      this.loading = false;
    }
  }

  kontoLabel(konto: string | null | undefined): string {
    if (!konto) return '';
    const hit = this.data.chartOfAccounts.find((a) => a.konto === konto);
    return hit ? `${hit.konto} – ${hit.nazivKonta}` : konto;
  }

  partnerLabel(code: string | null | undefined): string {
    if (!code) return '';
    const hit = this.data.codebookItems.find((c) => c.code === code);
    return hit ? `${hit.code} – ${hit.companyName || ''}` : code;
  }

  conditionSummary(rule: BankPostingRule): { label: string; value: string }[] {
    const parts: { label: string; value: string }[] = [];
    if (rule.opis1) parts.push({ label: 'Opis_1', value: rule.opis1 });
    if (rule.opis2) parts.push({ label: 'Opis_2', value: rule.opis2 });
    if (rule.opis3) parts.push({ label: 'Opis_3', value: rule.opis3 });
    if (rule.svrha) parts.push({ label: 'Svrha', value: rule.svrha });
    return parts;
  }

  openRuleDialog(rule?: BankPostingRule): void {
    const dialogData: BankPostingRuleDialogData = {
      clientID: this.data.clientID,
      companyAccount: this.data.companyAccount,
      rule,
      chartOfAccounts: this.data.chartOfAccounts,
      codebookItems: this.data.codebookItems,
    };
    this.dialog
      .open(BankPostingRuleDialogComponent, { data: dialogData, autoFocus: false })
      .afterClosed()
      .subscribe(async (saved) => {
        if (saved) {
          this.changed = true;
          await this.reload();
        }
      });
  }

  async deleteRule(rule: BankPostingRule): Promise<void> {
    if (!rule.id || this.deletingId) return;
    this.deletingId = rule.id;
    try {
      await this.ruleService.delete(rule);
      this.changed = true;
      this.rules = this.rules.filter((r) => r.id !== rule.id);
    } catch (err) {
      console.error('[BankPostingRules] Delete failed:', err);
      this.snackBar.open('Deleting the rule failed', '', { duration: 4000, panelClass: ['white-snackbar'] });
    } finally {
      this.deletingId = null;
    }
  }

  close(): void {
    this.dialogRef.close(this.changed);
  }
}
